import React, { useState, useEffect } from "react";
// @ts-ignore
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ListGroup, ListGroupItem } from "components/Shared";
import ReactDOM from "react-dom";

const KIND_LABELS: Record<string, string> = {
  create_form: "Create Form",
  document: "Upload a Document",
  boolean: "Boolean",
  date: "Date",
  accept: "Accept",
  inspection: "Inspection",
  quote: "Quote",
};
const kindLabel = (kind: string) => KIND_LABELS[kind] || kind;

// The clean form identity to show under a "Create Form" condition.
// Persisted/new conditions carry the clean `form_name` (the uploaded filename)
// and a suffix-stripped `form_code`. Legacy template conditions stored the raw
// `form_code` with the random base36 upload suffix (e.g. "tcf-d006-mra3wjwd"),
// so as a fallback we strip that trailing suffix — mirroring the backend
// `Form#display_code` regex (`-[a-z0-9]{7,9}` at the end) — to show the
// filename rather than the random string.
export const formLabel = (condition: any): string => {
  if (condition.form_name) return condition.form_name;
  const code = condition.form_code || "";
  return code.replace(/-[a-z0-9]{7,9}$/, "") || code;
};

// Deep-link to the form's preview page (same route as clicking a form in the
// library). Returns null when the condition has no linked form yet.
export const formPreviewHref = (condition: any): string | null =>
  condition.form_id ? `/forms/${condition.form_id}#preview` : null;

interface Props {
  conditions: any[];
  onUpdate: (conditions: any[]) => void;
  onAdd: () => void;
  // Opens the same modal as onAdd, carrying the row's condition so it prefills.
  onEdit?: (condition: any) => void;
}

// Helper function to properly merge styles for dragging
const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  // styles we need for the clone to be visible
  userSelect: "none",
  background: isDragging ? "#f0f0f0" : "transparent",
  
  // styles from react-beautiful-dnd
  ...draggableStyle
});

// Portal for rendering the dragged item outside modal boundaries
const portal: HTMLElement = document.createElement('div');
document.body.appendChild(portal);

const DraggableConditions = ({ conditions, onUpdate, onAdd, onEdit }: Props) => {
  const [localConditions, setLocalConditions] = useState(conditions);

  useEffect(() => {
    setLocalConditions(conditions);
  }, [conditions]);

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const reorderedConditions = Array.from(localConditions);
    const [removed] = reorderedConditions.splice(result.source.index, 1);
    reorderedConditions.splice(result.destination.index, 0, removed);

    // Update index for all conditions
    const conditionsWithNewIndex = reorderedConditions.map((condition, index) => ({
      ...condition,
      index: index
    }));

    setLocalConditions(conditionsWithNewIndex);
    onUpdate(conditionsWithNewIndex);
  };

  const handleRemove = (conditionId: string) => {
    const updatedConditions = localConditions.filter(c => c.id !== conditionId);
    setLocalConditions(updatedConditions);
    onUpdate(updatedConditions);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="conditions-droppable">
        {(provided: any, snapshot: any) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              background: snapshot.isDraggingOver ? 'rgba(0, 123, 255, 0.05)' : 'transparent',
            }}
          >
            <ListGroup
              resource="conditions"
              collection={localConditions}
              pagination={false}
              onAdd={onAdd}
              renderRow={(condition: any) => (
                <Draggable 
                  key={condition.id} 
                  draggableId={condition.id} 
                  index={localConditions.indexOf(condition)}
                >
                  {(provided: any, snapshot: any) => {
                    const child = (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <ListGroupItem
                          key={condition.id}
                          title={kindLabel(condition.kind)}
                          heading={condition.name}
                          subheading={
                            condition.kind === "create_form"
                              ? condition.form_code || condition.form_name
                                ? (
                                  <>
                                    Form:{" "}
                                    {formPreviewHref(condition) ? (
                                      <a
                                        className="condition-form-link"
                                        href={formPreviewHref(condition) as string}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {formLabel(condition)}
                                      </a>
                                    ) : (
                                      formLabel(condition)
                                    )}
                                  </>
                                )
                                : "Form: (none selected)"
                              : false
                          }
                          onEdit={onEdit ? () => onEdit(condition) : undefined}
                          onRemove={() => handleRemove(condition.id)}
                        />
                      </div>
                    );

                    // Render dragging item in a portal to escape modal overflow constraints
                    if (snapshot.isDragging) {
                      return ReactDOM.createPortal(child, portal);
                    }
                    return child;
                  }}
                </Draggable>
              )}
            />
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DraggableConditions;